import { useState } from 'react';

import type {
  BankAccount,
  BulkCreateBankStatementLines,
  CreateBankStatement,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  parseStatementGrid,
  STATEMENT_TEMPLATE_HEADERS,
  type ParsedStatementLine,
} from './recon-helpers';
import { downloadXlsx, fileToGrid } from '../../lib/xlsx';

interface Props {
  accounts: BankAccount[];
  onCreateStatement: (data: CreateBankStatement) => Promise<{ id: string }>;
  onCreateLines: (id: string, data: BulkCreateBankStatementLines) => Promise<unknown>;
  onAutoMatch: (id: string) => Promise<unknown>;
  pending: boolean;
  onClose: () => void;
}

export function ImportStatementModal({
  accounts,
  onCreateStatement,
  onCreateLines,
  onAutoMatch,
  pending,
  onClose,
}: Props) {
  const [bankAccountId, setBankAccountId] = useState(accounts[0]?.id ?? '');
  const [statementDate, setStatementDate] = useState('');
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [lines, setLines] = useState<ParsedStatementLine[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [runAuto, setRunAuto] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const movement = lines.reduce((s, l) => s + (l.credit ?? 0) - (l.debit ?? 0), 0);
  const expectedClosing = (Number(opening) || 0) + movement;
  const balanceOk = Math.round(expectedClosing * 100) === Math.round((Number(closing) || 0) * 100);
  const canSubmit =
    !!bankAccountId && !!statementDate && lines.length > 0 && parseErrors.length === 0 && !pending;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubmitError(null);
    const grid = await fileToGrid(file);
    const parsed = parseStatementGrid(grid);
    setLines(parsed.lines);
    setParseErrors(parsed.errors);
  }

  function downloadTemplate() {
    void downloadXlsx('bank-statement-template.xlsx', [
      STATEMENT_TEMPLATE_HEADERS.slice(),
      ['2026-05-02', 'ค่าธรรมเนียมโอนเงิน', '20', '', 'FEE-001', 'KBANK'],
      ['2026-05-05', 'รับชำระลูกค้า A', '', '50000', 'RV-2605-001', 'ลูกค้า A'],
    ]);
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      const created = await onCreateStatement({
        bankAccountId,
        statementDate,
        openingBalance: Number(opening) || 0,
        closingBalance: Number(closing) || 0,
      });
      await onCreateLines(created.id, { lines });
      if (runAuto) await onAutoMatch(created.id);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'นำเข้าไม่สำเร็จ');
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          maxHeight: '94vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>นำเข้า Bank Statement</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label>
            เลือกบัญชีธนาคาร
            <select
              aria-label="บัญชี"
              className="select"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              style={{ width: '100%', height: 38 }}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bank} {a.accNo} — {a.nameTh}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <label>
              วันที่ statement
              <input
                aria-label="วันที่"
                className="input"
                type="date"
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
                style={{ width: '100%', height: 38 }}
              />
            </label>
            <label>
              ยอดยกมา
              <input
                aria-label="ยอดยกมา"
                className="input"
                type="number"
                value={opening}
                onChange={(e) => setOpening(e.target.value)}
                style={{ width: '100%', height: 38 }}
              />
            </label>
            <label>
              ยอดปิด
              <input
                aria-label="ยอดปิด"
                className="input"
                type="number"
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                style={{ width: '100%', height: 38 }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              aria-label="ไฟล์"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => void onFile(e)}
            />
            <button className="btn sm ghost" onClick={downloadTemplate}>
              <Icon name="download" size={12} /> ดาวน์โหลดเทมเพลต
            </button>
          </div>
          {parseErrors.length > 0 && (
            <div
              style={{
                padding: 12,
                background: '#fce9e7',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {parseErrors.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </div>
          )}
          {lines.length > 0 && (
            <>
              <div className="meta">
                อ่านได้ {lines.length} รายการ · เคลื่อนไหวรวม {movement.toLocaleString()}
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 6,
                  fontSize: 14,
                  background: balanceOk ? '#e7f4ec' : '#fef3e8',
                  border: `1px solid ${balanceOk ? '#bbdebf' : '#f0dab0'}`,
                }}
              >
                {balanceOk
                  ? '✓ ยอดยกมา + เคลื่อนไหว = ยอดปิด (สมการตรง)'
                  : `⚠ ยอดยกมา + เคลื่อนไหว = ${expectedClosing.toLocaleString()} ไม่เท่ายอดปิด ${(Number(closing) || 0).toLocaleString()} — ตรวจไฟล์/ยอดก่อนนำเข้า`}
              </div>
            </>
          )}
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={runAuto}
              onChange={(e) => setRunAuto(e.target.checked)}
            />{' '}
            Run Auto-match ทันทีหลังนำเข้า
          </label>
          {submitError && (
            <div style={{ color: 'var(--negative)', fontSize: 14 }}>{submitError}</div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" disabled={!canSubmit} onClick={() => void submit()}>
            <Icon name="upload" size={14} /> นำเข้า
          </button>
        </div>
      </div>
    </div>
  );
}
